import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { NoteDialog } from "@/components/EntityDialogs";
import { Mic, Type, PenLine, Sparkles, Edit, Trash2, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function Notes() {
  const { openVoice } = useOutletContext();
  const { notes, addTask, deleteNote } = useFgp();
  const [filter, setFilter] = useState("all");
  const [editNote, setEditNote] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const filtered = notes.filter((n) => filter === "all" || (n.type || "voice") === filter);
  const openNew = () => { setEditNote(null); setDialogOpen(true); };
  const openEdit = (n) => { setEditNote(n); setDialogOpen(true); };

  return (
    <div>
      <PageHeader
        title="Notes"
        subtitle="Capture notes by voice or text, edit them anytime, and turn them into tasks."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={openVoice} className="gap-2" data-testid="notes-record"><Mic className="h-4 w-4" /> Record</Button>
            <Button onClick={openNew} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="notes-new"><PenLine className="h-4 w-4" /> Write note</Button>
          </div>
        }
      />

      <div className="flex gap-1.5 mb-6">
        {[["all", "All"], ["voice", "Voice"], ["text", "Typed"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`notes-filter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === k ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <PenLine className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No notes yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Write a note or record a voice memo — we'll draft tasks for you.</p>
          <Button onClick={openNew} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-notes-new"><PenLine className="h-4 w-4" /> Write your first note</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((n, i) => (
            <motion.div key={n.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="group bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all" data-testid={`note-card-${n.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${(n.type || "voice") === "voice" ? "bg-neutral-50 text-neutral-600" : "bg-slate-100 text-slate-600"}`}>
                  {(n.type || "voice") === "voice" ? <Mic className="h-4 w-4" /> : <Type className="h-4 w-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{n.title}</p>
                  <p className="text-xs text-slate-400">{n.date} · {(n.type || "voice") === "voice" ? n.duration : "typed"}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(n)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-neutral-600 hover:bg-slate-50" data-testid={`note-edit-${n.id}`}><Edit className="h-4 w-4" /></button>
                  <button onClick={() => setToDelete(n)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{n.transcript}</p>
              {n.suggestedTasks?.length > 0 && (
                <div className="mt-4 rounded-lg border border-neutral-100 bg-neutral-50/40 p-3">
                  <div className="flex items-center gap-1.5 mb-2"><Sparkles className="h-3.5 w-3.5 text-neutral-600" /><span className="text-xs font-semibold text-slate-700">Suggested tasks</span></div>
                  <div className="space-y-1.5">
                    {n.suggestedTasks.map((t, j) => (
                      <div key={j} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-neutral-400" /> {t}</span>
                        <button onClick={() => { addTask(t); toast.success("Task created"); }} data-testid={`note-create-task-${n.id}-${j}`}
                          className="text-xs text-neutral-600 font-medium flex items-center gap-1 hover:underline"><Plus className="h-3 w-3" /> Add</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <NoteDialog open={dialogOpen} onOpenChange={setDialogOpen} note={editNote} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-note"
              onClick={() => { deleteNote(toDelete.id); toast.success("Note deleted"); setToDelete(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
